import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Popover from '@mui/material/Popover'
import Slider from '@mui/material/Slider'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid2'
import CaleoIconButton from 'Components/reusable/IconButtons/CaleoIconButton'
import React, { useEffect, useState } from 'react'
import CopyAllIcon from '@mui/icons-material/CopyAll'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import { useTranslation } from 'react-i18next'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { personAPI } from 'api/PersonAPI'
import { useUser } from 'hooks'
import { salesPoolAPI } from 'api/SalesPoolAPI'
import { ISalesPoolItem } from 'types/salesPoolInterfaces'
import { useSalesPool } from 'Components/General/SalesPoolProvider/SalesPoolProvider'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { IError } from 'types/error'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import YellowOutlinedButton from 'Components/reusable/Buttons/YellowOutlinedButton'
import colors from 'constants/colors'

/**
 * SalesPool modal.
 *
 * @returns SalesPool modal component.
 * @notExported
 */
const SalesPoolModal: React.FC = () => {
  const { t } = useTranslation()
  const { features } = useUser()
  const { editedSalesPool, setEditedSalesPool, salesPools, setSalesPools } = useSalesPool()
  const { setSuccessNotificationPopup } = useNotificationPopup()

  const [images, setImages] = useState<{ personId: number; image: string }[]>([])
  const [expiringWeekCount, setExpiringWeekCount] = useState<number>(4)
  const [linkText, setLinkText] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [salesPoolAnonymous, setSalesPoolAnonymous] = useState<boolean>(false)
  const [allocationsVisible, setAllocationsVisible] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    if (editedSalesPool && editedSalesPool.SalesPoolItems && editedSalesPool.SalesPoolItems.length) {
      ;(async () => {
        try {
          const imagesMap: { personId: number; image: string }[] = []
          if (editedSalesPool.SalesPoolItems) {
            for (const salesPoolItem of editedSalesPool.SalesPoolItems) {
              if (salesPoolItem.Person && salesPoolItem.personId) {
                if (!imagesMap.find(image => image.personId === salesPoolItem.personId)) {
                  const image = await personAPI.getProfileImage(salesPoolItem.personId, controller)
                  imagesMap.push({ personId: salesPoolItem.personId, image: image })
                }
              }
            }
          }
          {
            setImages(imagesMap)

            setTitle(editedSalesPool.title)
            setExpiringWeekCount(editedSalesPool.durationInWeeks)
            if (editedSalesPool.publicId && editedSalesPool.publicId.length > 1) {
              setLinkText(
                `${window.location.protocol}//${window.location.hostname}${
                  window.location.hostname === 'localhost' ? `:${window.location.port}` : ''
                }/salespool/${editedSalesPool.publicId}`
              )
            } else {
              setLinkText('')
            }
            setSalesPoolAnonymous(editedSalesPool.anonymous)
            setAllocationsVisible(editedSalesPool.allocationsVisible)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    } else {
      setOpen(false)
    }

    return () => {
      controller.abort()
    }
  }, [editedSalesPool])

  const openSalespoolPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpen(false)
  }

  const deleteSalesPoolitem = (item: ISalesPoolItem) => {
    if (editedSalesPool && editedSalesPool.SalesPoolItems) {
      const newItems = editedSalesPool.SalesPoolItems.filter(poolItem => {
        if (item.personId) {
          return poolItem.personId !== item.personId
        } else {
          return poolItem.uploadId !== item.uploadId
        }
      })

      setEditedSalesPool({ ...editedSalesPool, SalesPoolItems: newItems })
    }
  }

  const getChip = (item: ISalesPoolItem, index: number) => {
    const label = (
      <span>{item.Person ? `${item.Person.firstName} ${item.Person.lastName}` : item.Upload?.originalname}</span>
    )
    return (
      <Chip
        key={index}
        style={{ margin: 5 }}
        avatar={
          item.Person ? (
            <Avatar alt="" src={images.find(image => image.personId === item.personId)?.image ?? ''} />
          ) : undefined
        }
        label={label}
        onDelete={() => deleteSalesPoolitem(item)}
      />
    )
  }

  const generateSalesPoolLink = async () => {
    if (editedSalesPool && editedSalesPool.SalesPoolItems && editedSalesPool.SalesPoolItems.length) {
      try {
        const result = await salesPoolAPI.createSalesPool({
          title,
          durationInWeeks: expiringWeekCount,
          anonymous: salesPoolAnonymous,
          poolItems: editedSalesPool.SalesPoolItems,
          allocationsVisible: allocationsVisible,
        })

        {
          const poolLink = `${window.location.protocol}//${window.location.hostname}${
            window.location.hostname === 'localhost' ? `:${window.location.port}` : ''
          }/salespool/${result.publicId}`

          setLinkText(poolLink)
          setEditedSalesPool(result)
          setSalesPools([...salesPools, result])
        }
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setSuccessNotificationPopup()
      }
    }
  }

  const updateSalesPoolLink = async () => {
    if (
      editedSalesPool &&
      editedSalesPool.id &&
      editedSalesPool.SalesPoolItems &&
      editedSalesPool.SalesPoolItems.length
    ) {
      try {
        const result = await salesPoolAPI.updateSalesPool(editedSalesPool.id, {
          title,
          durationInWeeks: expiringWeekCount,
          anonymous: salesPoolAnonymous,
          poolItems: editedSalesPool.SalesPoolItems,
          allocationsVisible: allocationsVisible,
        })

        {
          const poolLink = `${window.location.protocol}//${window.location.hostname}${
            window.location.hostname === 'localhost' ? `:${window.location.port}` : ''
          }/salespool/${result.publicId}`

          setLinkText(poolLink)
          setEditedSalesPool(result)

          const index = salesPools.findIndex(pool => pool.id === result.id)
          const newSalesPools = [...salesPools]
          newSalesPools[index] = result
          setSalesPools(newSalesPools)
        }
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setSuccessNotificationPopup()
      }
    }
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <>
      {editedSalesPool && editedSalesPool.SalesPoolItems && editedSalesPool.SalesPoolItems.length > 0 && (
        <Grid container alignItems="center" spacing={2}>
          <Grid sx={{ visibility: open ? 'hidden' : 'visible' }} size="auto">
            <Button
              data-testid="button-sales-pool-open"
              onClick={openSalespoolPopover}
              size="small"
              variant="contained"
              color="primary"
            >
              {t('open')}
            </Button>
          </Grid>
          {!open && (
            <Grid size="grow">
              <Grid container spacing={2} alignItems="center">
                <Grid>
                  {t('selection')}: {editedSalesPool.SalesPoolItems.map((item, i) => getChip(item, i))}{' '}
                  {`(${editedSalesPool.SalesPoolItems.length})`}
                </Grid>
              </Grid>
            </Grid>
          )}
          <Popover
            id="salespool-popover"
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            slotProps={{
              paper: { sx: { backgroundColor: colors.secondaryBackground, width: '100%' } },
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" spacing={2} p={2}>
              {editedSalesPool.SalesPoolItems.filter(item => item.uploadId)?.length > 0 && salesPoolAnonymous && (
                <Grid size={12}>
                  <CardContentText type={'error'}>{t('salesPool.anonymousFileInfo')}</CardContentText>
                </Grid>
              )}
              <Grid size={12}>
                <Grid container justifyContent="space-between">
                  <Grid size={6}>
                    <CaleoInputLabel label={t('salesPool.titleField')} />
                    <TextField
                      data-testid="field-sales-pool-title"
                      value={title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                      margin="dense"
                      label={null}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  {editedSalesPool.SalesPoolItems?.length && editedSalesPool.publicId?.length && (
                    <Grid size={1}>
                      <YellowOutlinedButton
                        label={t('salesPool.reset')}
                        clickAction={() => setEditedSalesPool(undefined)}
                        valid
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                  <Grid sx={{ width: '250px' }}>
                    <CaleoInputLabel label={t('salesPool.expiresIn', { date: expiringWeekCount })} />
                    <Slider
                      aria-label="duration"
                      data-testid="slider-sales-pool-duration"
                      value={expiringWeekCount}
                      onChange={(_e, value) => {
                        if (!Array.isArray(value)) setExpiringWeekCount(value)
                      }}
                      min={1}
                      max={8}
                      step={1}
                      valueLabelDisplay="auto"
                      defaultValue={4}
                    />
                  </Grid>
                  {features.includes('allocations') && (
                    <Grid>
                      <CaleoInputLabel label={t('salesPool.allocationsVisible')} />
                      <Switch
                        checked={allocationsVisible}
                        data-testid="switch-allocations-visible"
                        onChange={() => setAllocationsVisible(!allocationsVisible)}
                      />
                    </Grid>
                  )}
                  <Grid>
                    <CaleoInputLabel label={t('salesPool.anonymous')} />
                    <Switch
                      checked={salesPoolAnonymous}
                      data-testid="switch-sales-pool-anonymous"
                      onChange={() => setSalesPoolAnonymous(!salesPoolAnonymous)}
                    />
                  </Grid>
                  {linkText.length > 0 && (
                    <Grid>
                      <Grid container spacing={1} alignItems="center">
                        <Grid>{linkText}</Grid>
                        <Grid>
                          <CaleoIconButton
                            testId="button-sales-pool-copy-link"
                            icon={<CopyAllIcon />}
                            clickAction={() => navigator.clipboard.writeText(linkText)}
                            tooltip={t('salesPool.copyToClipboard')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid>
                    {linkText.length > 0 && editedSalesPool && editedSalesPool.id ? (
                      <CaleoPrimaryButton
                        testId="button-sales-pool-update-link"
                        valid={true}
                        label={t('salesPool.updateSalesPool')}
                        clickAction={updateSalesPoolLink}
                      />
                    ) : (
                      <CaleoPrimaryButton
                        testId="button-sales-pool-generate-link"
                        valid={true}
                        label={t('salesPool.generateLink')}
                        clickAction={generateSalesPoolLink}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid size="auto">
                <Button
                  data-testid="button-sales-pool-close"
                  onClick={handleClose}
                  size="small"
                  variant="contained"
                  color="primary"
                >
                  {t('close')}
                </Button>
              </Grid>
              <Grid size="grow">
                <Grid container spacing={2} alignItems="center">
                  <Grid>
                    {t('selection')}: {editedSalesPool.SalesPoolItems.map((item, idx) => getChip(item, idx))}{' '}
                    {`(${editedSalesPool.SalesPoolItems.length})`}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Popover>
        </Grid>
      )}
    </>
  )
}

export default SalesPoolModal
